import React, { useCallback, useEffect, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { getSecrets } from "../../config";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { parseBToCMessage } from "protico-sdk";
import {
  updateRoomMessages,
  updateRoomTyping,
} from "../../store/reducers/bToCState";
import { TBToCMessage } from "../../types/types";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";
import { TPreviewMessage, TWhiteboardMessage } from "protico-sdk";
import api, { API_POST_B_TO_C_READ, getProticoClient } from "../../utils/api";
export interface IMessageReference {
  message_id: string;
  /** the address of the sender */
  sender_address: string;
  messageText: string;
}
interface IBtoCWebsocketContext {
  sendTyping: (toDomain: string) => void;
  sendStopTyping: (toDomain: string) => void;
  sendMessage: (message: TBToCMessage) => void;
  readyState: ReadyState;
  handleSetNextReference: (message_reference: IMessageReference | null) => void;
  displayLatestMessage: TWhiteboardMessage;
  handleSetShouldScrollToBottom: () => void;
  nextReference: IMessageReference | null;
  isLatestMessageExist: boolean;
  handleSetMessageList: (newMessageList: any[]) => void;
  handleNewMessages: (newMessageList: any[]) => void;
  shouldScrollToBottom: boolean;
  cleanShouldScrollToBottom: () => void;
}

export const BtoCWebsocketContext = React.createContext<IBtoCWebsocketContext>({
  sendTyping: () => {},
  sendStopTyping: () => {},
  sendMessage: () => {},
  readyState: ReadyState.CONNECTING,
  handleSetNextReference: () => {},
  displayLatestMessage: null,
  handleSetShouldScrollToBottom: () => {},
  nextReference: null,
  isLatestMessageExist: false,
  handleSetMessageList: () => {},
  handleNewMessages: () => {},
  shouldScrollToBottom: false,
  cleanShouldScrollToBottom: () => {},
});

export const BtoCWebsocketProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  //Public API that will echo messages sent to it back to the client
  const { NODE_ENV } = getSecrets();
  const [socketUrl, setSocketUrl] = useState(null);
  const [nextReference, setMessageReference] =
    useState<IMessageReference | null>(null);
  const [displayLatestMessage, setDisplayLatestMessage] =
    useState<TWhiteboardMessage>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] =
    useState<boolean>(false);
  const [isLatestMessageExist, setIsLatestMessageExist] =
    useState<boolean>(true);
  const [messageList, setMessageList] = useState<TWhiteboardMessage[]>([]);
  const [latestMessageseen, setLatestMessageseen] = useState<any>(null);
  const [isMessageLoading, setIsMessageLoading] = useState<boolean>(true);
  const { roomInformations, lastBToCTarget } = useAppSelector(
    (state) => state.bToCState
  );
  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    onOpen: () => console.log("b2c socket opened"),
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: (closeEvent) => true,
    reconnectAttempts: 10,
    //attemptNumber will be 0 the first time it attempts to reconnect, so this equation results in a reconnect pattern of 1 second, 2 seconds, 4 seconds, 8 seconds, and then caps at 10 seconds until the maximum number of attempts is reached
    reconnectInterval: (attemptNumber) =>
      Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
  });
  const dispatch = useAppDispatch();
  const { isProticoAuthenticated, address, realAddress } = useWeb3Auth();

  useEffect(() => {
    if (!address) return;
    if (!isProticoAuthenticated) return;
    setSocketUrl(
      (NODE_ENV === "production" ? "wss://" : "ws://") +
        window.location.host +
        "/ws/btoc/" +
        realAddress
    );
  }, [isProticoAuthenticated, address]);

  useEffect(() => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data);
      if (data.type === "typing") {
        dispatch(
          updateRoomTyping({
            toDomain: data.payload.address,
            isOtherTyping: true,
          })
        );
      } else if (data.type === "stop_typing") {
        dispatch(
          updateRoomTyping({
            toDomain: data.payload.address,
            isOtherTyping: false,
          })
        );
      } else if (data.type === "message") {
        // Get a message from the server
        const message = parseBToCMessage(data.payload);

        let unread_count = 0;

        if (message.toDomain != lastBToCTarget.toDomain) {
          for (const [_, roomInformation] of Object.entries(roomInformations)) {
            let messages: Array<TBToCMessage> = [];
            for (const [id, message] of Object.entries(
              roomInformation.messageList
            )) {
              messages.push(message);
            }
            messages.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );
            if (messages.length > 0) {
              if (unread_count < messages[0].message_count) {
                unread_count = messages[0].message_count;
              }
            }
          }
        } else {
          getProticoClient().api.post(API_POST_B_TO_C_READ, {
            from_address: message.fromAddress,
            to_domain: message.toDomain,
          })
          unread_count = -1;
        }

        dispatch(
          updateRoomMessages({
            toDomain: message.toDomain,
            messageText: {
              ...message,
              message_count: unread_count + 1,
            },
          })
        );
      }
    }
  }, [lastMessage]);

  const sendTyping = useCallback(
    (toDomain: string) =>
      sendJsonMessage({
        type: "typing",
        payload: { to_domain: toDomain },
      }),
    []
  );
  const sendStopTyping = useCallback(
    (toDomain: string) =>
      sendJsonMessage({
        type: "stop_typing",
        payload: { to_domain: toDomain },
      }),
    []
  );
  const sendMessage = useCallback(
    (message: TBToCMessage) => {
      const payloadDict = {
        id: message.id,
        message_type: message.messageType,
        message_text: message.messageText,
        from_address: message.fromAddress,
        to_domain: message.toDomain,
        logoUrl: message.logoUrl,
      } as any;
      if (nextReference && nextReference.message_id) {
        payloadDict.message_reference = {
          message_id: nextReference.message_id,
        };
      }
      sendJsonMessage({
        type: "message",
        payload: payloadDict,
      });
      setMessageReference(null);
    },
    [nextReference]
  );

  const handleSetNextReference = useCallback(
    (message_reference: IMessageReference | null) => {
      setMessageReference(message_reference);
    },
    []
  );

  const getTimeFromCreatedAt = (message: TWhiteboardMessage) =>
    new Date(message.createdAt).getTime();

  const isNewMessage = (
    latestMessageseen: TWhiteboardMessage,
    newMessage: TWhiteboardMessage
  ) => {
    return (
      latestMessageseen === null ||
      getTimeFromCreatedAt(newMessage) > getTimeFromCreatedAt(latestMessageseen)
    );
  };

  const handleSetMessageList = useCallback(
    (newMessageList: TWhiteboardMessage[]) => {
      setMessageList(newMessageList);
      if (
        newMessageList.length > 0 &&
        isNewMessage(latestMessageseen, newMessageList[0])
      ) {
        setLatestMessageseen(newMessageList[0]);
      }
      setIsMessageLoading(false);
    },
    [latestMessageseen?.id]
  );

  const handleNewMessages = useCallback(
    (newMessageList: any[]) => {
      const domain = newMessageList[0].to_domain;
      const processedMessages = newMessageList.map((historyMessage) => {
        try {
          return {
            ...historyMessage,
          } as unknown as TBToCMessage;
        } catch (_) {
          return undefined;
        }
      });

      processedMessages.forEach((message) => {
        const message_parsed = parseBToCMessage(message);
        dispatch(
          updateRoomMessages({
            toDomain: domain,
            messageText: { ...message_parsed, sended: true },
          })
        );
      });
    },
    [JSON.stringify(messageList), latestMessageseen?.id]
  );

  const cleanShouldScrollToBottom = useCallback(() => {
    setShouldScrollToBottom(false);
  }, []);
  const handleSetShouldScrollToBottom = useCallback(() => {
    setShouldScrollToBottom(true);
  }, []);

  return (
    <BtoCWebsocketContext.Provider
      value={{
        sendTyping,
        sendStopTyping,
        sendMessage,
        readyState,
        handleSetNextReference,
        displayLatestMessage,
        handleSetShouldScrollToBottom,
        nextReference,
        isLatestMessageExist,
        handleSetMessageList,
        handleNewMessages,
        shouldScrollToBottom,
        cleanShouldScrollToBottom,
      }}
    >
      {children}
    </BtoCWebsocketContext.Provider>
  );
};
